import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookOpen, Trash2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/apiClient";
import { JUZZ_LIST } from "@/lib/juzz";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// Header "clip" icon that opens the user's saved bookmarks. Auth-only.
export const BookmarksMenu = () => {
  const { user } = useAuth();
  const isAuthed = !!user && typeof user === "object";
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/bookmarks");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next && isAuthed) load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/me/bookmarks/${id}`);
      setItems(prev => prev.filter(b => b.id !== id));
      toast.success("Bookmark removed");
    } catch {
      toast.error("Couldn't remove bookmark");
    }
  };

  const labelFor = (b) => {
    if (b.label) return b.label;
    const j = JUZZ_LIST.find(x => x.number === b.juzz);
    const jName = j ? `Juzz ${j.number} · ${j.name_translit}` : (b.juzz ? `Juzz ${b.juzz}` : "Bookmark");
    if (b.page) return `${jName} · page ${b.page}`;
    return jName;
  };

  const linkFor = (b) => {
    const jNum = b.juzz || 1;
    return b.page
      ? `/quran/mushaf/juzz/${jNum}#page-${b.page}`
      : `/quran/mushaf/juzz/${jNum}`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full relative"
          data-testid="bookmarks-trigger-btn"
          aria-label="Saved bookmarks"
          title="Saved bookmarks"
        >
          <Bookmark className="w-4 h-4" />
          {isAuthed && items.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold inline-flex items-center justify-center" data-testid="bookmarks-count">
              {items.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[320px] p-0 overflow-hidden"
        data-testid="bookmarks-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <p className="font-display text-base font-medium inline-flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-accent" /> Your bookmarks
          </p>
          {isAuthed && items.length > 0 && (
            <span className="text-xs text-foreground/60">{items.length}</span>
          )}
        </div>

        {!isAuthed ? (
          <div className="p-6 text-center" data-testid="bookmarks-signin-prompt">
            <p className="text-sm text-foreground/70">Sign in to sync and view your bookmarks across devices.</p>
            <Button asChild className="mt-4 pill-btn bg-primary hover:bg-primary/90" onClick={() => setOpen(false)}>
              <Link to="/login"><LogIn className="w-4 h-4 mr-1" /> Sign in</Link>
            </Button>
          </div>
        ) : loading ? (
          <div className="p-6 text-center text-sm text-foreground/60" data-testid="bookmarks-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center" data-testid="bookmarks-empty">
            <p className="text-sm text-foreground/70">No bookmarks yet.</p>
            <p className="text-xs text-foreground/50 mt-1">Tap the bookmark icon on any ayah while reading.</p>
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto divide-y divide-border" data-testid="bookmarks-list">
            {items.map(b => (
              <li key={b.id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-muted/60 group">
                <Link
                  to={linkFor(b)}
                  onClick={() => setOpen(false)}
                  className="flex-1 min-w-0 flex items-center gap-2.5"
                  data-testid={`bookmark-item-${b.id}`}
                >
                  <span className="w-8 h-8 rounded-full bg-primary/10 border border-accent/40 inline-flex items-center justify-center shrink-0">
                    <BookOpen className="w-3.5 h-3.5 text-primary" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">{labelFor(b)}</span>
                    {b.note ? (
                      <span className="block text-[11px] text-foreground/55 truncate">{b.note}</span>
                    ) : (
                      <span className="block text-[11px] text-foreground/55">
                        {new Date(b.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  className="p-1.5 rounded-md text-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                  data-testid={`bookmark-delete-${b.id}`}
                  aria-label="Remove bookmark"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};
